import React from "react";
import { FiUsers, FiDollarSign, FiShoppingBag, FiCreditCard, FiArrowDown } from "react-icons/fi";

const stages = [
    { key: "visitors", title: "Visitors", totalLabel: "Visitors", icon: FiUsers, accent: "sky" },
    { key: "pricing", title: "Pricing", totalLabel: "Views", icon: FiDollarSign, accent: "violet" },
    { key: "plans", title: "Plan Selection", totalLabel: "Selections", icon: FiShoppingBag, accent: "amber" },
    { key: "checkout", title: "Checkout", totalLabel: "Starts", icon: FiShoppingBag, accent: "orange" },
    { key: "payments", title: "Payments", totalLabel: "Payments", icon: FiCreditCard, accent: "emerald" },
];

const nodeBg = {
    sky: "bg-sky-500",
    violet: "bg-violet-500",
    amber: "bg-amber-500",
    orange: "bg-orange-500",
    emerald: "bg-emerald-500",
};

const tierStyle = (conversion) => {
    if (conversion >= 70) return { line: "bg-emerald-400", chip: "bg-emerald-50 text-emerald-700" };
    if (conversion >= 40) return { line: "bg-amber-400", chip: "bg-amber-50 text-amber-700" };
    return { line: "bg-rose-400", chip: "bg-rose-50 text-rose-700" };
};

const compact = new Intl.NumberFormat("en", { notation: "compact" });

const MarketingFunnel = ({ data }) => {
    const computed = stages.map((stage, index) => {
        const stageData =
            stage.key === "visitors"
                ? { unique: data?.visitors || 0, total: data?.visitors || 0 }
                : data?.[stage.key] || { unique: 0, total: 0 };

        const previousUnique =
            index === 0
                ? stageData.unique
                : stage.key === "pricing"
                ? data?.visitors || 0
                : data?.[stages[index - 1].key]?.unique || 0;

        const conversion =
            index === 0 ? 100 : previousUnique > 0 ? Math.round((stageData.unique / previousUnique) * 100) : 0;
        const dropoff = index === 0 ? 0 : Math.max(previousUnique - stageData.unique, 0);

        return { ...stage, unique: stageData.unique, total: stageData.total, conversion, dropoff };
    });

    return (
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-indigo-50/40 to-white p-5">
            <h2 className="text-sm font-semibold text-slate-900">Marketing Funnel</h2>
            <p className="mb-5 text-xs text-slate-500">Visitor conversion through your acquisition funnel</p>

            {/* Connected stepper — desktop */}
            <div className="hidden sm:grid sm:grid-cols-5">
                {computed.map((stage, index) => {
                    const tier = tierStyle(stage.conversion);
                    const nextTier = index < computed.length - 1 ? tierStyle(computed[index + 1].conversion) : null;

                    return (
                        <div key={stage.key} className="group relative flex flex-col items-center rounded-lg px-2 pb-3 pt-0 text-center transition-colors hover:bg-white">
                            {/* connector line segments, centered on node */}
                            {index !== 0 && (
                                <div className={`absolute left-0 right-1/2 top-4 h-0.5 ${tier.line}`} />
                            )}
                            {nextTier && (
                                <div className={`absolute left-1/2 right-0 top-4 h-0.5 ${nextTier.line}`} />
                            )}

                            {/* node */}
                            <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full text-white shadow-sm ring-4 ring-white ${nodeBg[stage.accent]}`}>
                                <stage.icon size={14} />
                            </div>

                            {/* transition chip, sits right under the line */}
                            <div className="mt-1.5 h-4">
                                {index !== 0 && (
                                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tier.chip}`}>
                                        {stage.conversion}%
                                    </span>
                                )}
                            </div>

                            <h3 className="mt-1 truncate text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                {stage.title}
                            </h3>

                            <div className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
                                {compact.format(stage.unique)}
                            </div>

                            <div className="text-[11px] text-slate-400">
                                {stage.key !== "visitors" ? `${compact.format(stage.total)} ${stage.totalLabel}` : "\u00A0"}
                            </div>

                            {index !== 0 && (
                                <span className="mt-1 flex items-center gap-0.5 text-[10px] font-medium text-rose-400">
                                    <FiArrowDown size={9} />
                                    {compact.format(stage.dropoff)} lost
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Compact vertical timeline — mobile */}
            <div className="space-y-3 sm:hidden">
                {computed.map((stage, index) => {
                    const tier = tierStyle(stage.conversion);
                    const isLast = index === computed.length - 1;

                    return (
                        <div key={stage.key} className="flex gap-3">
                            <div className="flex flex-col items-center">
                                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white ${nodeBg[stage.accent]}`}>
                                    <stage.icon size={13} />
                                </div>
                                {!isLast && <div className={`mt-1 w-0.5 flex-1 ${tierStyle(computed[index + 1].conversion).line}`} style={{ minHeight: 14 }} />}
                            </div>

                            <div className="flex-1 pb-1">
                                <div className="flex items-baseline justify-between">
                                    <span className="text-xs font-medium text-slate-600">{stage.title}</span>
                                    {index !== 0 && (
                                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tier.chip}`}>
                                            {stage.conversion}%
                                        </span>
                                    )}
                                </div>
                                <div className="text-lg font-bold tabular-nums text-slate-900">
                                    {compact.format(stage.unique)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MarketingFunnel;